/**
 * MacroSessionCoordinator — up to `maxConcurrentSessions` compiled-package runs per Playjector.
 *
 * Each start fetches a grant (`MACROPACKAGE_REQUEST`), starts it natively, and follows the session's
 * replies to a terminal view. Playjector stays authoritative for capacity, leases and watch budget;
 * this coordinator only refuses a 9th local start and maps every outcome to an operator-visible code.
 */
import {
  MACRO_PACKAGE_CANCEL_ALL_RUN_ID, MACRO_PACKAGE_LIMITS, macroTriggerSchema,
  type CompiledPackageRef, type MacroLiveDetection, type MacroPackageRunReply, type MacroRuntimeMode,
} from '@playcastdotio/Messaging';
import type { MacroPackageGrantRequest, MacroPackageSocketLoader } from '../../services/playjector/MacroPackageSocketLoader';
import type { NativeMacroPackageBridge } from '../../services/playjector/NativeMacroPackageBridge';
import {
  cancelSessionView, failSessionView, isTerminalSessionState, reduceSessionReply,
  type MacroSessionErrorCode, type MacroSessionView,
} from './macroSessionState';

export type { MacroSessionView } from './macroSessionState';
export const DEFAULT_COMPILED_CACHE_ROOT = 'C:\\ProgramData\\Playcast\\macro-cache';
const RETAINED_SESSIONS = 64;
const SHA256 = /^[0-9a-f]{64}$/;

export interface MacroSessionCoordinatorDeps {
  loader: Pick<MacroPackageSocketLoader, 'request'>;
  bridge: Pick<NativeMacroPackageBridge, 'start' | 'validate' | 'cancel' | 'onReply' | 'onDisconnected'>;
  ids: () => string;
  now: () => number;
  compiledCacheRoot?: string;
}
export interface MacroSessionStartInput {
  campaignId: string; trigger: string; mode: MacroRuntimeMode; variant?: 'authoritative' | 'preview';
  packageSha256?: string; liveDetection?: MacroLiveDetection;
}
type SessionSource = { kind: 'grant'; request: MacroPackageGrantRequest } | { kind: 'path'; packageSha256: string };
interface SessionEntry {
  view: MacroSessionView; startedAtMs: number; source: SessionSource; mode: MacroRuntimeMode;
  liveDetection?: MacroLiveDetection; nativeRunId?: string; refetched: boolean; cancelRequested: boolean;
}

export class MacroSessionCoordinator {
  private readonly entries = new Map<string, SessionEntry>();
  private readonly byNativeRunId = new Map<string, SessionEntry>();
  private readonly listeners = new Set<(sessions: readonly MacroSessionView[]) => void>();
  private snapshot: readonly MacroSessionView[] = [];

  public constructor(private readonly deps: MacroSessionCoordinatorDeps) {
    deps.bridge.onReply((reply) => this.handleReply(reply));
    deps.bridge.onDisconnected(() => this.failAll('native_ipc_disconnected'));
  }

  public start(input: MacroSessionStartInput): Promise<MacroSessionView> {
    const trigger = macroTriggerSchema.safeParse(input.trigger);
    const variant = input.variant ?? 'authoritative';
    const request: MacroPackageGrantRequest = { campaignId: input.campaignId, trigger: trigger.success ? trigger.data : 'launch',
      variant, ...(input.packageSha256 ? { packageSha256: input.packageSha256 } : {}) };
    const entry = this.admit(input.trigger, variant, { kind: 'grant', request }, input.mode, input.liveDetection);
    if (input.liveDetection !== 'continuous' || input.mode !== 'local-shadow') this.fail(entry, 'macro_graph_migration_required');
    if (!trigger.success) this.fail(entry, 'package_not_found');
    return isTerminalSessionState(entry.view.state) ? Promise.resolve(entry.view) : this.launch(entry);
  }

  /** Ask Playjector to load/decode the campaign's exact compiled package; this never starts playback. */
  public async validatePackage(input: { campaignId: string; trigger: string; packageSha256: string }): Promise<{ ok: true } | { ok: false; error: string }> {
    if (!SHA256.test(input.packageSha256)) return { ok: false, error: 'package_digest_mismatch' };
    const trigger = macroTriggerSchema.safeParse(input.trigger);
    if (!trigger.success) return { ok: false, error: 'invalid_command' };
    const grant = await this.deps.loader.request({ campaignId: input.campaignId, trigger: trigger.data,
      variant: 'authoritative', packageSha256: input.packageSha256 });
    if (!grant.ok) return { ok: false, error: grant.error };
    const reply = await this.deps.bridge.validate({ runId: this.deps.ids(), packageSha256: grant.packageSha256,
      package: { source: 'grant', grant: grant.grant, packageKey: grant.packageKey } });
    return reply.outcome === 'validated' ? { ok: true } : { ok: false, error: reply.errorCode ?? 'host_validation_failed' };
  }

  /** Runs an imported `.pcmc` from `CompiledCacheRoot()/<sha>` (no grant). */
  public startFromPath(sha: string, mode: MacroRuntimeMode): Promise<MacroSessionView> {
    const entry = this.admit('imported', 'preview', { kind: 'path', packageSha256: sha }, mode);
    this.fail(entry, 'macro_graph_migration_required');
    if (!SHA256.test(sha)) this.fail(entry, 'package_not_found');
    return isTerminalSessionState(entry.view.state) ? Promise.resolve(entry.view) : this.launch(entry);
  }

  public async cancel(runId: string): Promise<void> {
    const all = runId === MACRO_PACKAGE_CANCEL_ALL_RUN_ID;
    const targets = [...this.entries.values()].filter((entry) => (all || entry.view.runId === runId) && !isTerminalSessionState(entry.view.state));
    for (const entry of targets) {
      entry.cancelRequested = true;
      if (!entry.nativeRunId) this.update(entry, cancelSessionView(entry.view));
    }
    const native = targets.filter((entry) => entry.nativeRunId);
    if (!all && native.length === 0) return;
    const reply = await this.deps.bridge.cancel(all ? MACRO_PACKAGE_CANCEL_ALL_RUN_ID : native[0].nativeRunId as string);
    for (const entry of native) {
      this.update(entry, reply.outcome === 'cancelled' ? cancelSessionView(entry.view) : reduceSessionReply(entry.view, reply));
    }
    // A terminal failure is observable, but does not acknowledge that held input was released.
    if (reply.outcome !== 'cancelled') throw new Error(reply.errorCode ?? 'input_release_unconfirmed');
  }

  public sessions(): readonly MacroSessionView[] {
    return this.snapshot;
  }

  public subscribe(listener: (sessions: readonly MacroSessionView[]) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => { this.listeners.delete(listener); };
  }

  private admit(trigger: string, variant: MacroSessionView['variant'], source: SessionSource, mode: MacroRuntimeMode,
    liveDetection?: MacroLiveDetection): SessionEntry {
    const running = [...this.entries.values()].filter((entry) => !isTerminalSessionState(entry.view.state)).length;
    const entry: SessionEntry = { view: { runId: this.deps.ids(), trigger, variant, state: 'fetching' }, startedAtMs: this.deps.now(),
      source, mode, liveDetection, refetched: false, cancelRequested: false };
    this.entries.set(entry.view.runId, entry);
    if (running >= MACRO_PACKAGE_LIMITS.maxConcurrentSessions) this.fail(entry, 'native_session_capacity');
    else this.publish();
    return entry;
  }

  private async launch(entry: SessionEntry): Promise<MacroSessionView> {
    const ref = await this.resolvePackage(entry);
    if (!ref || entry.cancelRequested || isTerminalSessionState(entry.view.state)) return this.settleCancelled(entry);
    const nativeRunId = this.deps.ids();
    entry.nativeRunId = nativeRunId;
    this.byNativeRunId.set(nativeRunId, entry);
    this.update(entry, { ...entry.view, state: 'starting' });
    const admission = await this.deps.bridge.start({ runId: nativeRunId, mode: entry.mode, package: ref.package,
      packageSha256: ref.packageSha256, ...(entry.liveDetection ? { liveDetection: entry.liveDetection } : {}) });
    if (entry.nativeRunId !== nativeRunId) return entry.view;
    this.apply(entry, admission);
    // A cancel that raced the admission found nothing to stop natively; stop the admitted run now.
    if (entry.cancelRequested && admission.outcome === 'running') void this.deps.bridge.cancel(nativeRunId);
    return entry.view;
  }

  private async resolvePackage(entry: SessionEntry): Promise<{ package: CompiledPackageRef; packageSha256: string } | null> {
    if (entry.source.kind === 'path') {
      const root = (this.deps.compiledCacheRoot ?? DEFAULT_COMPILED_CACHE_ROOT).replace(/[\\/]+$/, '');
      return { package: { source: 'path', directory: `${root}\\${entry.source.packageSha256}` }, packageSha256: entry.source.packageSha256 };
    }
    this.update(entry, { ...entry.view, state: 'fetching' });
    const grant = await this.deps.loader.request(entry.source.request);
    if (!grant.ok) { this.fail(entry, grant.error); return null; }
    return { package: { source: 'grant', grant: grant.grant, packageKey: grant.packageKey }, packageSha256: grant.packageSha256 };
  }

  private handleReply(reply: MacroPackageRunReply): void {
    const entry = this.byNativeRunId.get(reply.runId);
    if (entry) return this.apply(entry, reply);
    if (reply.runId !== MACRO_PACKAGE_CANCEL_ALL_RUN_ID) {
      console.warn('[MacroSessionCoordinator] Dropping package-run reply for unknown run', { runId: reply.runId, outcome: reply.outcome });
    }
  }

  /** `playback_asset_unavailable` (expired or refused grant) earns exactly one fresh grant and restart. */
  private apply(entry: SessionEntry, reply: MacroPackageRunReply): void {
    const refetch = reply.outcome === 'failed' && reply.errorCode === 'playback_asset_unavailable'
      && entry.source.kind === 'grant' && !entry.refetched && !entry.cancelRequested && !isTerminalSessionState(entry.view.state);
    if (!refetch) return this.update(entry, reduceSessionReply(entry.view, reply));
    entry.refetched = true;
    if (entry.nativeRunId) this.byNativeRunId.delete(entry.nativeRunId);
    entry.nativeRunId = undefined;
    void this.launch(entry);
  }

  private settleCancelled(entry: SessionEntry): MacroSessionView {
    if (entry.cancelRequested) this.update(entry, cancelSessionView(entry.view));
    return entry.view;
  }

  private failAll(errorCode: MacroSessionErrorCode): void {
    for (const entry of this.entries.values()) entry.view = failSessionView(entry.view, errorCode);
    this.publish();
  }

  private fail(entry: SessionEntry, errorCode: MacroSessionErrorCode): void {
    this.update(entry, failSessionView(entry.view, errorCode));
  }

  private update(entry: SessionEntry, view: MacroSessionView): void {
    if (view === entry.view) return;
    entry.view = view;
    this.publish();
  }

  /** Terminal sessions stay listed (and their late replies stay known) until the retention cap evicts them. */
  private publish(): void {
    const terminal = [...this.entries.values()].filter((entry) => isTerminalSessionState(entry.view.state))
      .sort((left, right) => left.startedAtMs - right.startedAtMs);
    for (const stale of terminal.slice(0, Math.max(0, this.entries.size - RETAINED_SESSIONS))) {
      this.entries.delete(stale.view.runId);
      if (stale.nativeRunId) this.byNativeRunId.delete(stale.nativeRunId);
    }
    this.snapshot = [...this.entries.values()].map((entry) => entry.view);
    for (const listener of this.listeners) listener(this.snapshot);
  }
}
